import React, { useContext, useEffect, useMemo, useState } from 'react';
import { matchPath } from 'react-router';
import { useHistory } from 'react-router-dom';

import { Link } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import SvgIcon from '@material-ui/core/SvgIcon';
import LeftArrowIcon from '@material-ui/icons/ArrowBackIosRounded';
import RightArrowIcon from '@material-ui/icons/ArrowForwardIosRounded';
import cx from 'classnames';
import classnames from 'classnames';
import {
  PATHWAYS_START_URL,
  TEACHERS_HOME_URL,
  TEACHER_ASSIGNMENTS_V2_URL
} from 'config/urls';
import logoSmall from 'images/logo-small.svg';
import logoBig from 'images/logo.svg';
import { isNull, map } from 'lodash';

import TeacherAssignmentCreate from 'pages/Teachers/Assignments/Create';
import SideNavItemWrapper from 'pages/Teachers/shared/TeacherPageLayout/components/SideNavItemWrapper';
import { colors } from 'theme/palette';
import { useDialog } from 'utils/hooks';
import { useIsMobile } from 'utils/resizing';

import AssignmentDialogContext from 'components/AssignmentDialogContext';
import Dialog from 'components/Dialog';
import FreeTrialReminder from 'components/FreeTrialReminder';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import styles from './styles.module.scss';
import { getLeftNavigationItems } from './utils';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';

const SideNav = ({ open, toggle, lgScreen }) => {
  const [isWide, setIsWide] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useContext(UserContext);
  const [, setTick] = useState(0);

  const logoRedirectUrl =
    isMobile && user.is_pathways_user
      ? PATHWAYS_START_URL
      : HOME_PAGE_ENABLED
      ? TEACHERS_HOME_URL
      : TEACHER_ASSIGNMENTS_V2_URL;

  const {
    isOpened: isNoAssignmentsDialogOpened,
    closeDialog: closeNoAssignmentsDialog
  } = useDialog();

  const history = useHistory();

  const { isAssignmentDialogOpened, setIsAssignmentDialogOpened } = useContext(
    AssignmentDialogContext
  );

  const toggleNavigationOnTabletResolution = () => {
    if (!lgScreen) {
      toggle();
    }
  };

  const leftNavigationItems = useMemo(() => {
    if (user) {
      return getLeftNavigationItems({ user, isMobile });
    }
    return [];
  }, [user, isMobile]);

  const isSelected = ({ url, currentPath }) => {
    if (url.startsWith('/pathways/insights')) {
      return matchPath(currentPath, {
        path: '/pathways/insights',
        exact: false
      });
    }

    if (url.startsWith('/pathways')) {
      return (
        currentPath.startsWith('/pathways') &&
        !currentPath.includes('/insights')
      );
    }

    if (url.startsWith('/teacher/insights')) {
      return matchPath(currentPath, {
        path: '/teacher/insights',
        exact: false
      });
    }

    if (url.startsWith('/teacher/bulk-upload')) {
      return matchPath(currentPath, {
        path: '/teacher/bulk-upload',
        exact: false
      });
    }

    if (
      url.startsWith('/teacher/V2/assignments') ||
      url.startsWith('/teacher/home')
    ) {
      return (
        currentPath.startsWith('/teacher/V2/assignments') ||
        currentPath.startsWith('/teacher/assignments') ||
        currentPath.startsWith('/teacher/home')
      );
    }

    return !isNull(
      matchPath(currentPath, {
        path: url,
        exact: false
      })
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1); // trigger re-render
    }, 60000); // update every minute

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Drawer
        open={open}
        variant={lgScreen ? 'permanent' : 'temporary'}
        classes={{
          paper: classnames(styles.drawer, { [styles.small]: !open })
        }}
        onClose={toggle}
      >
        <div className={styles.menuContent}>
          <div className={styles.menuActions}>
            <Link
              className={styles.logo}
              href={logoRedirectUrl}
              onClick={toggleNavigationOnTabletResolution}
            >
              <SideNavItemWrapper open={open} text="Home">
                <img src={open ? logoBig : logoSmall} alt="Logo" />
              </SideNavItemWrapper>
            </Link>

            <List component="div" classes={{ root: styles.menuList }}>
              {map(
                leftNavigationItems,
                ({
                  key,
                  text,
                  shortText,
                  url,
                  Icon,
                  dataIntercomTargetName
                }) => {
                  const selected = isSelected({
                    url,
                    currentPath: history.location.pathname
                  });

                  if (key === 'create') {
                    return (
                      <div
                        key={key}
                        onClick={() => {
                          toggleNavigationOnTabletResolution();
                          setIsAssignmentDialogOpened(true);
                        }}
                        data-intercom-target={
                          dataIntercomTargetName
                            ? dataIntercomTargetName
                            : undefined
                        }
                      >
                        <SideNavItemWrapper open={open} text={text}>
                          <ListItem
                            button
                            selected={isAssignmentDialogOpened}
                            classes={{
                              root: styles.listItem,
                              selected: styles.selected
                            }}
                          >
                            <SvgIcon
                              component={Icon}
                              htmlColor={
                                isAssignmentDialogOpened
                                  ? colors.blue5
                                  : colors.white
                              }
                            />
                            {open && (
                              <Typography
                                classes={{ root: styles.listItemText }}
                                variant="H-TEXT-2"
                                color={
                                  isAssignmentDialogOpened
                                    ? colors.blue5
                                    : colors.white
                                }
                              >
                                {shortText || text}
                              </Typography>
                            )}
                          </ListItem>
                        </SideNavItemWrapper>
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={key}
                      href={url}
                      onClick={toggleNavigationOnTabletResolution}
                      className={styles.listItemLink}
                    >
                      <SideNavItemWrapper open={open} text={text}>
                        <ListItem
                          button
                          selected={selected}
                          classes={{
                            root: styles.listItem,
                            selected: styles.selected
                          }}
                        >
                          <Icon
                            htmlColor={selected ? colors.blue5 : colors.white}
                          />
                          {open && (
                            <Typography
                              classes={{ root: styles.listItemText }}
                              variant="H-TEXT-2"
                              color={selected ? colors.blue5 : colors.white}
                            >
                              {text}
                            </Typography>
                          )}
                        </ListItem>
                      </SideNavItemWrapper>
                    </Link>
                  );
                }
              )}
            </List>
          </div>

          <IconButton onClick={toggle}>
            {open ? (
              <LeftArrowIcon
                htmlColor={colors.white}
                classes={{ root: styles.arrowIcon }}
              />
            ) : (
              <RightArrowIcon
                htmlColor={colors.white}
                classes={{ root: styles.arrowIcon }}
              />
            )}
          </IconButton>
        </div>

        {user.is_in_free_trial && <FreeTrialReminder displayProgress={true} />}

        <a
          href="https://www.edlight.com/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.upgradeLink}
          onClick={(e) => e.stopPropagation()}
        >
          Terms of Use
        </a>
        <a
          href="https://www.edlight.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.upgradeLink}
          onClick={(e) => e.stopPropagation()}
        >
          Privacy Policy
        </a>
      </Drawer>
      {isNoAssignmentsDialogOpened && (
        <Dialog
          open
          onClose={closeNoAssignmentsDialog}
          classes={{ paper: styles.dialogPaper }}
        >
          <Typography variant="H-TEXT-2" color={colors.blue1}>
            No Assignments to Review!
          </Typography>
        </Dialog>
      )}

      {isAssignmentDialogOpened && (
        <Dialog
          open
          alignTop
          onClose={() => setIsAssignmentDialogOpened(false)}
          classes={{
            paper: cx(styles.assignmentCreateDialog, {
              [styles.wideDialog]: isWide
            })
          }}
        >
          <TeacherAssignmentCreate onWidthChange={setIsWide} />
        </Dialog>
      )}
    </>
  );
};

export default SideNav;
